import { __extends } from "tslib";
import { BasicPartition, NodePool, View } from '@awayjs/view';
import { MethodMaterial } from '@awayjs/materials';
import { DisplayObjectContainer } from '../display/DisplayObjectContainer';
import { DisplayObject } from '../display/DisplayObject';
import { Billboard } from '../display/Billboard';
import { Settings } from '../Settings';
import { Rectangle, Point, PerspectiveProjection, CoordinateSystem, Vector3D, Transform, ColorUtils } from '@awayjs/core';
import { Stage, BitmapImage2D, _Stage_BitmapImage2D, BlendMode } from '@awayjs/stage';
import { DefaultRenderer, RenderGroup, Style } from '@awayjs/renderer';
// empty matrix for transform reset
var TMP_POINT = new Point(0, 0);
/**
 *
 */
var SceneImage2D = /** @class */ (function (_super) {
    __extends(SceneImage2D, _super);
    /**
     * Creates a BitmapImage2D object with a specified width and height. If you
     * specify a value for the <code>fillColor</code> parameter, every pixel in
     * the bitmap is set to that color.
     *
     * <p>By default, the bitmap is created as transparent, unless you pass
     * the value <code>false</code> for the transparent parameter. After you
     * create an opaque bitmap, you cannot change it to a transparent bitmap.
     * Every pixel in an opaque bitmap uses only 24 bits of color channel
     * information. If you define the bitmap as transparent, every pixel uses 32
     * bits of color channel information, including an alpha transparency
     * channel.</p>
     *
     * @param width       The width of the bitmap image in pixels.
     * @param height      The height of the bitmap image in pixels.
     * @param transparent Specifies whether the bitmap image supports per-pixel
     *                    transparency. The default value is <code>true</code>
     *                    (transparent). To create a fully transparent bitmap,
     *                    set the value of the <code>transparent</code>
     *                    parameter to <code>true</code> and the value of the
     *                    <code>fillColor</code> parameter to 0x00000000(or to
     *                    0). Setting the <code>transparent</code> property to
     *                    <code>false</code> can result in minor improvements
     *                    in rendering performance.
     * @param fillColor   A 32-bit ARGB color value that you use to fill the
     *                    bitmap image area. The default value is
     *                    0xFFFFFFFF(solid white).
     */
    function SceneImage2D(width, height, transparent, fillColor, powerOfTwo, stage) {
        if (transparent === void 0) { transparent = true; }
        if (fillColor === void 0) { fillColor = 0xffffffff; }
        if (powerOfTwo === void 0) { powerOfTwo = true; }
        if (stage === void 0) { stage = null; }
        var _this = _super.call(this, width, height, transparent, null, powerOfTwo, stage) || this;
        _this._initalFillColor = null;
        _this._lastUsedFill = null;
        _this._internalSync = false;
        _this._msaaNeedDrop = false;
        _this._enforceMSAASupport = false;
        /*private*/ _this._antialiasQuality = Settings.ALLOW_FORCE_MSAA;
        _this._clearFromDispose = false;
        _this._initalFillColor = fillColor;
        _this._lastUsedFill = fillColor;
        return _this;
    }
    SceneImage2D.getImage = function (width, height, transparent, fillColor, powerOfTwo, stage, msaa) {
        if (transparent === void 0) { transparent = true; }
        if (fillColor === void 0) { fillColor = 0xffffffff; }
        if (powerOfTwo === void 0) { powerOfTwo = true; }
        if (stage === void 0) { stage = null; }
        if (msaa === void 0) { msaa = false; }
        var result = new SceneImage2D(width, height, transparent, fillColor, powerOfTwo, stage);
        if (!msaa) {
            result._msaaNeedDrop = true;
            result._antialiasQuality = 0;
        }
        else {
            result._enforceMSAASupport = true;
        }
        return result;
    };
    SceneImage2D.tryStoreImage = function (image, stage) {
        stage.filterManager.pushTemp(image);
    };
    SceneImage2D.getTemp = function (width, height, stage, msaa) {
        if (msaa === void 0) { msaa = false; }
        var image = stage.filterManager.popTemp(width, height, msaa && Settings.ALLOW_FORCE_MSAA > 1);
        image.antialiasQuality = msaa ? Settings.ALLOW_FORCE_MSAA : 0;
        return image;
    };
    SceneImage2D.prototype._dropMSAA = function () {
        if (this._msaaNeedDrop || !this.canUseMSAAInternaly) {
            return;
        }
        this._enforceMSAASupport = false;
        this._msaaNeedDrop = true;
        // force dispose texture and buffers
        // MSAA not stored in pool
        if (this.wasUpload) {
            // super unload not call sync, we call it
            _super.prototype.unload.call(this);
        }
        console.debug('[SceneImage2D Experemental] Drop MSAA support because a setPixel* operation called after upload.', this.id);
    };
    Object.defineProperty(SceneImage2D.prototype, "canUseMSAAInternaly", {
        get: function () {
            if (this._enforceMSAASupport) {
                return true;
            }
            var minW = Settings.MSAA_MINIMAL_IMAGE_SIZE;
            var minH = Settings.MSAA_MINIMAL_IMAGE_SIZE;
            if (this._stage) {
                minH = Math.min(this._stage.height, minH);
                minW = Math.min(this._stage.width, minW);
            }
            return (this.width >= minW &&
                this.height >= minH &&
                !this._msaaNeedDrop);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SceneImage2D.prototype, "antialiasQuality", {
        get: function () {
            return this.canUseMSAAInternaly ? this._antialiasQuality : 0;
        },
        enumerable: true,
        configurable: true
    });
    SceneImage2D.prototype.syncData = function (async) {
        var _this = this;
        if (async === void 0) { async = false; }
        if (async && this._asyncRead) {
            return this._asyncRead;
        }
        if (!async && this._asyncRead) {
            throw '[SceneImage2D] Synced read not allowed while async read is requested!';
        }
        this.applySymbol();
        // update data from pixels from GPU
        if (!this._imageDataDirty) {
            return async ? Promise.resolve(false) : false;
        }
        var context = this._stage.context;
        this._stage.setRenderTarget(this, false);
        // when we call syncData, we already loose other data
        // not require apply symbol etc, because it already should be applied
        if (!this._data) {
            this._data = new Uint8ClampedArray(this.width * this.height * 4);
        }
        // mark that this internal call, avoid reqursion loop
        this._internalSync = true;
        this._asyncRead = context.drawToBitmapImage2D(this, false, async);
        this._internalSync = false;
        this._stage.setRenderTarget(null);
        if (!async) {
            this._imageDataDirty = false;
            // we store pixel buffer already as PMA.
            // we should prevent unpack what already is PMA
            this._unpackPMA = false;
            return true;
        }
        return this._asyncRead.then(function (_status) {
            _this._imageDataDirty = false;
            _this._unpackPMA = false;
            _this._asyncRead = null;
            return true;
        });
    };
    /* overide internal */ SceneImage2D.prototype.getDataInternal = function (constructEmpty, skipSync) {
        if (constructEmpty === void 0) { constructEmpty = true; }
        if (skipSync === void 0) { skipSync = false; }
        // if sync called, check that this is requried
        if (!skipSync && this._imageDataDirty && !this._internalSync) {
            // sync data already should fill _data
            this.syncData(false);
            return this._data;
        }
        if (this._initalFillColor === null) {
            return _super.prototype.getDataInternal.call(this, constructEmpty, true);
        }
        // disable empty buffer filling
        // and check that buffer is empyt (has now symbol or alpha)
        var data = _super.prototype.getDataInternal.call(this, false, true);
        // if it empty, fill with initlal value
        if (!data) {
            // fill rect constuct buffer inside
            _super.prototype.fillRect.call(this, this.rect, this._initalFillColor);
            this._initalFillColor = null;
            return this._data;
        }
        return data;
    };
    Object.defineProperty(SceneImage2D.prototype, "assetType", {
        /**
         *
         * @returns {string}
         */
        get: function () {
            return SceneImage2D.assetType;
        },
        enumerable: true,
        configurable: true
    });
    SceneImage2D.prototype.createRenderer = function () {
        //create the projection
        var projection = new PerspectiveProjection();
        projection.coordinateSystem = CoordinateSystem.RIGHT_HANDED;
        projection.originX = -1;
        projection.originY = 1;
        //create the view
        SceneImage2D._root = new DisplayObjectContainer();
        SceneImage2D._rootNode = NodePool.getRootNode(SceneImage2D._root, BasicPartition);
        SceneImage2D._renderer =
            RenderGroup
                .getInstance(new View(projection, this._stage, null, null, null, true), DefaultRenderer)
                .getRenderer(SceneImage2D._rootNode.partition);
        //SceneImage2D._root.partition = SceneImage2D._renderer.partition;
        //SceneImage2D._renderer.antiAlias = Settings.ALLOW_FORCE_MSAA;
        //setup the projection
        SceneImage2D._renderer.disableClear = true;
        SceneImage2D._renderer.view.backgroundAlpha = 0;
        SceneImage2D._renderer.view.projection = projection;
        SceneImage2D._renderer.view.projection.transform = new Transform();
        SceneImage2D._renderer.view.projection.transform.scaleTo(1, -1, 1);
        SceneImage2D._renderer.view.projection.transform.moveTo(0, 0, -1000);
        SceneImage2D._renderer.renderableSorter = null; //new RenderableSort2D();
    };
    SceneImage2D.prototype.createBillboardRenderer = function () {
        //create the projection
        var projection = new PerspectiveProjection();
        projection.coordinateSystem = CoordinateSystem.RIGHT_HANDED;
        projection.originX = -1;
        projection.originY = 1;
        //create the view
        SceneImage2D._billboardRoot = new DisplayObjectContainer();
        SceneImage2D._billboardRenderer = RenderGroup
            .getInstance(new View(projection, this._stage, null, null, null, true), DefaultRenderer)
            .getRenderer(NodePool.getRootNode(SceneImage2D._billboardRoot, BasicPartition).partition);
        //SceneImage2D._billboardRoot.partition = SceneImage2D._billboardRenderer.partition;
        //SceneImage2D._renderer.antiAlias = Settings.ALLOW_FORCE_MSAA;
        //setup the projection
        SceneImage2D._billboardRenderer.disableClear = true;
        SceneImage2D._billboardRenderer.view.backgroundAlpha = 0;
        SceneImage2D._billboardRenderer.view.projection = projection;
        SceneImage2D._billboardRenderer.view.projection.transform = new Transform();
        SceneImage2D._billboardRenderer.view.projection.transform.moveTo(0, 0, -1000);
        SceneImage2D._billboardRenderer.view.projection.transform.lookAt(new Vector3D());
        SceneImage2D._billboardRenderer.renderableSorter = null; //new RenderableSort2D();
        var mat = new MethodMaterial(new BitmapImage2D(128, 128, true, 0x0));
        mat.bothSides = true;
        mat.alphaBlending = true;
        SceneImage2D._billboard = new Billboard(mat);
        SceneImage2D._billboard.style = new Style();
        SceneImage2D._billboardRoot.addChild(SceneImage2D._billboard);
    };
    /**
     * Frees memory that is used to store the BitmapImage2D object.
     *
     * <p>When the <code>dispose()</code> method is called on an image, the width
     * and height of the image are set to 0. All subsequent calls to methods or
     * properties of this BitmapImage2D instance fail, and an exception is thrown.
     * </p>
     *
     * <p><code>BitmapImage2D.dispose()</code> releases the memory occupied by the
     * actual bitmap data, immediately(a bitmap can consume up to 64 MB of
     * memory). After using <code>BitmapImage2D.dispose()</code>, the BitmapImage2D
     * object is no longer usable and an exception may be thrown if
     * you call functions on the BitmapImage2D object. However,
     * <code>BitmapImage2D.dispose()</code> does not garbage collect the BitmapImage2D
     * object(approximately 128 bytes); the memory occupied by the actual
     * BitmapImage2D object is released at the time the BitmapImage2D object is
     * collected by the garbage collector.</p>
     *
     */
    SceneImage2D.prototype.dispose = function () {
        this._clearFromDispose = true;
        this.dropAllReferences();
        this.unmarkToUnload();
        this.unuseWeakRef();
        // drop buffer, because is big
        this._data = null;
        this._locked = false;
        _super.prototype.dispose.call(this);
        this._clearFromDispose = false;
    };
    SceneImage2D.prototype.unload = function () {
        var _this = this;
        // query async unload
        var t = this.syncData(true);
        // strict quard
        if (typeof t !== 'boolean') {
            t.then(function () { return _super.prototype.unload.call(_this); });
            return;
        }
        _super.prototype.unload.call(this);
    };
    SceneImage2D.prototype.deepClone = function (from) {
        this.copyPixels(from, this._rect, new Point(0, 0));
    };
    /**
     * Fills a rectangular area of pixels with a specified ARGB color.
     *
     * @param rect  The rectangular area to fill.
     * @param color The ARGB color value that fills the area. ARGB colors are
     *              often specified in hexadecimal format; for example,
     *              0xFF336699.
     * @throws TypeError The rect is null.
     */
    SceneImage2D.prototype.fillRect = function (rect, color) {
        this.dropAllReferences();
        var argb = ColorUtils.float32ColorToARGB(color);
        var alpha = this._transparent ? argb[0] / 255 : 1;
        var isCrop = rect !== this._rect && !this._rect.equals(rect);
        this._stage.setRenderTarget(this, true, 0, 0, true);
        this._stage.setScissor(rect);
        // we shure that color is fully filled when there are not any crops
        this._lastUsedFill = isCrop ? null : color;
        this._stage.clear((argb[1] / 0xff) * alpha, (argb[2] / 0xff) * alpha, (argb[3] / 0xff) * alpha, alpha);
        this._stage.setScissor(null);
        this._imageDataDirty = true;
    };
    /**
     * Provides a fast routine to perform pixel manipulation between images with
     * no stretching, rotation, or color effects. This method copies a
     * rectangular area of a source image to a rectangular area of the same size
     * at the destination point of the destination BitmapImage2D object.
     *
     * <p>If you include the <code>alphaBitmap</code> and <code>alphaPoint</code>
     * parameters, you can use a secondary image as an alpha source for the
     * source image. If the source image has alpha data, both sets of alpha data
     * are used to composite pixels from the source image to the destination
     * image. The <code>alphaPoint</code> parameter is the point in the alpha
     * image that corresponds to the upper-left corner of the source rectangle.
     * Any pixels outside the intersection of the source image and alpha image
     * are not copied to the destination image.</p>
     *
     * <p>The <code>mergeAlpha</code> property controls whether or not the alpha
     * channel is used when a transparent image is copied onto another
     * transparent image. To copy pixels with the alpha channel data, set the
     * <code>mergeAlpha</code> property to <code>true</code>. By default, the
     * <code>mergeAlpha</code> property is <code>false</code>.</p>
     *
     * @param source The input bitmap image from which to copy pixels.
     *                         The source image can be a different BitmapImage2D
     *                         instance, or it can refer to the current
     *                         BitmapImage2D instance.
     * @param sourceRect       A rectangle that defines the area of the source
     *                         image to use as input.
     * @param destPoint        The destination point that represents the
     *                         upper-left corner of the rectangular area where
     *                         the new pixels are placed.
     * @param alphaBitmapData  A secondary, alpha BitmapImage2D object source.
     * @param alphaPoint       The point in the alpha BitmapImage2D object source
     *                         that corresponds to the upper-left corner of the
     *                         <code>sourceRect</code> parameter.
     * @param mergeAlpha       To use the alpha channel, set the value to
     *                         <code>true</code>. To copy pixels with no alpha
     *                         channel, set the value to <code>false</code>.
     * @throws TypeError The sourceBitmapImage2D, sourceRect, destPoint are null.
     */
    SceneImage2D.prototype.copyPixels = function (source, sourceRect, destPoint, alphaBitmapData, alphaPoint, mergeAlpha) {
        this._lastUsedFill = null;
        this.dropAllReferences();
        this.unmarkToUnload();
        // need drop alpha from source when target is not has alpha
        mergeAlpha = this.transparent !== source.transparent || mergeAlpha;
        // CPU based copy, not require run GPU based copy
        // block is equal, one of image not uploaded yet
        // and source image no require SYNC
        if (!source._imageDataDirty &&
            sourceRect.equals(this._rect) &&
            this._rect.equals(source.rect) &&
            !mergeAlpha && (!this.wasUpload || !source.wasUpload)) {
            var data = source.getDataInternal(true);
            // inline, instead of setPixels, because it use a lot of checks
            if (this._data) {
                this._data.set(data);
            }
            else {
                this._data = data.slice();
            }
            // we should sync this, because we can apply PMA twice, that not needed for us
            this._unpackPMA = source._unpackPMA;
            // we should reset initial color, because not require fill it after copyPixel
            this._initalFillColor = null;
            this._imageDataDirty = false;
            this.invalidateGPU();
            return;
        }
        if (source.width * source.height <= Settings.CPU_COPY_PIXELS_COUNT && !mergeAlpha) {
            // todo Not implemented yet, need to implement and check performance change
            // 	i think that increase some small operation performance,
            // 	because copy by GPU required upload array to VRAM,
            // 	and games that use set/get pixels and copyPixels only for math process not required use GPU copy
        }
        if (this._initalFillColor !== null) {
            this.fillRect(this._rect, this._initalFillColor);
            this._initalFillColor = null;
        }
        var compositeSource = source;
        // merge alpha with source
        if (alphaBitmapData) {
            compositeSource = this._stage.filterManager.popTemp(source.width, source.height);
            //this._stage.filterManager.copyPixels(source, compositeSource, source.rect, source.rect.topLeft);
            this._stage.filterManager.copyPixels(alphaBitmapData, compositeSource, new Rectangle(alphaPoint.x, alphaPoint.y, sourceRect.width, sourceRect.height), new Point(0, 0));
            this._stage.filterManager.copyPixels(source, compositeSource, source.rect, new Point(0, 0), true, 'alpha_back');
        }
        this._stage.filterManager.copyPixels(compositeSource, this, sourceRect, destPoint, mergeAlpha);
        if (alphaBitmapData) {
            this._stage.filterManager.pushTemp(compositeSource);
        }
        this._imageDataDirty = true;
    };
    SceneImage2D.prototype.threshold = function (source, sourceRect, destPoint, operation, threshold, color, mask, copySource) {
        this._lastUsedFill = null;
        this.dropAllReferences();
        this.unmarkToUnload();
        this._stage.threshold(source, this, sourceRect, destPoint, operation, threshold, color, mask, copySource);
        this._imageDataDirty = true;
    };
    SceneImage2D.prototype.applyFilter = function (source, sourceRect, destPoint, filter) {
        if (!Settings.USE_UNSAFE_FILTERS || !filter || !filter.filterName) {
            return false;
        }
        this.dropAllReferences(false);
        var result = this._stage.filterManager.applyFilter(source, this, sourceRect, destPoint, filter.filterName, filter);
        this._imageDataDirty = result;
        return result;
    };
    SceneImage2D.prototype.colorTransform = function (rect, colorTransform) {
        this.dropAllReferences();
        this.unmarkToUnload();
        this._lastUsedFill = null;
        this._stage.colorTransform(this, this, rect, colorTransform);
        this._imageDataDirty = true;
    };
    SceneImage2D.prototype.setPixel = function (x, y, color) {
        // we can't upload buffer in MSAA texture after creating - only render it and get
        if (this.canUseMSAAInternaly) {
            this._dropMSAA();
        }
        _super.prototype.setPixel.call(this, x, y, color);
    };
    SceneImage2D.prototype.setPixel32 = function (x, y, color) {
        // we can't upload buffer in MSAA texture after creating - only render it and get
        if (this.canUseMSAAInternaly) {
            this._dropMSAA();
        }
        _super.prototype.setPixel32.call(this, x, y, color);
    };
    SceneImage2D.prototype.setPixels = function (rect, buffer) {
        // we can't upload buffer in MSAA texture after creating - only render it and get
        if (this.wasUpload && this.canUseMSAAInternaly) {
            this._dropMSAA();
        }
        _super.prototype.setPixels.call(this, rect, buffer);
    };
    SceneImage2D.prototype.clear = function () {
        var _this = this;
        // we call clear in parent class from dispose, call direct
        if (this._clearFromDispose) {
            _super.prototype.clear.call(this);
            return;
        }
        // clear should drop abstraction, but we can't doing this if unloaded
        var t = this.syncData(true);
        // not require unload, we already doings this
        this.unmarkToUnload();
        this.lastUsedTime = -1;
        if (typeof t === 'boolean') {
            this.wasUpload = false;
            _super.prototype.clear.call(this);
            return;
        }
        t.then(function () {
            _this.wasUpload = false;
            _super.prototype.clear.call(_this);
        });
    };
    /**
     * @inheritdoc
     */
    SceneImage2D.prototype.getPixel32 = function (x, y) {
        this.syncData();
        return _super.prototype.getPixel32.call(this, x, y);
    };
    /**
     * @inheritdoc
     */
    SceneImage2D.prototype.getPixel = function (x, y) {
        var result = this.getPixel32(x, y);
        return result & 0x00ffffff;
    };
    SceneImage2D.prototype.draw = function (source, matrix, colorTransform, blendMode, clipRect, smoothing) {
        /* eslint-enable */
        this.dropAllReferences();
        this.unmarkToUnload();
        if (source instanceof DisplayObject) {
            this._drawAsDisplay(source, matrix, colorTransform, blendMode, clipRect, smoothing);
        }
        else {
            this._drawAsBitmap(source, matrix, colorTransform, blendMode, clipRect, smoothing);
        }
        this._lastUsedFill = null;
        this._imageDataDirty = true;
    };
    SceneImage2D._mapSupportedBlendMode = function (blendMode) {
        if (blendMode === void 0) { blendMode = ''; }
        switch (blendMode) {
            case null:
            case '':
            case BlendMode.NORMAL:
            case BlendMode.LAYER:
                return BlendMode.LAYER;
            case BlendMode.MULTIPLY:
            case BlendMode.ADD:
            case BlendMode.ALPHA:
                return blendMode;
        }
        //console.debug("[ImageBitmap] Unsupport BlendMode", blendMode);
        return BlendMode.LAYER;
    };
    SceneImage2D.prototype._drawAsBitmap = function (source, matrix, colorTransform, blendMode, _clipRect, smoothing) {
        if (!SceneImage2D._billboardRenderer) {
            this.createBillboardRenderer();
        }
        if (this._initalFillColor !== null) {
            this.fillRect(this._rect, this._initalFillColor);
            this._initalFillColor = null;
        }
        var renderer = SceneImage2D._billboardRenderer;
        var root = SceneImage2D._billboardRoot;
        var billboard = SceneImage2D._billboard;
        billboard.sampler.smooth = smoothing;
        renderer.disableClear = true;
        renderer.view.target = this;
        renderer.view.projection.scale = 1000 / this.rect.height;
        billboard.material.style.image = source;
        // not all blend modes can be used for rendering
        billboard.material.blendMode = SceneImage2D._mapSupportedBlendMode(blendMode);
        billboard.material.useColorTransform = !!colorTransform;
        if (matrix) {
            var m = root.transform.matrix3D;
            m.identity();
            m._rawData[0] = matrix.a;
            m._rawData[1] = -matrix.b;
            m._rawData[4] = matrix.c;
            m._rawData[5] = -matrix.d;
            m._rawData[12] = matrix.tx;
            m._rawData[13] = this.rect.height - matrix.ty;
            root.transform.invalidateComponents();
        }
        else {
            root.transform.rotateTo(0, 0, 0);
            root.transform.scaleTo(1, -1, 1);
            root.transform.moveTo(0, this.rect.height, 0);
        }
        root.transform.colorTransform = colorTransform;
        //render
        renderer.render();
    };
    SceneImage2D.prototype._drawAsDisplay = function (source, matrix, colorTransform, blendMode, _clipRect, _smoothing) {
        if (blendMode === void 0) { blendMode = ''; }
        // default global blend mode
        blendMode = blendMode || BlendMode.LAYER;
        if (!SceneImage2D._renderer) {
            this.createRenderer();
        }
        var root = SceneImage2D._root;
        var rootNode = SceneImage2D._rootNode;
        var renderer = SceneImage2D._renderer;
        // when we should use MSAA, we will create temporary image and draw to it
        var internal = this.canUseMSAAInternaly;
        var nativeMSAA = (!source.isAsset(Billboard) &&
            this._stage.context.glVersion === 2 && // can be used because a webgl2
            Settings.ALLOW_FORCE_MSAA > 1 && // because a quality is more that 1
            !internal); // and not internal
        // target image for rendering.
        var target = this;
        // lazy filling
        // we require fill image with initial color, because we not doing this immediate
        if (!nativeMSAA && this._initalFillColor !== null) {
            this.fillRect(this._rect, this._initalFillColor);
            this._initalFillColor = null;
        }
        // we should run compositor when blendMode !== LAYER (default)
        // or when we use MSAA + fill is not flat.
        var useBlend = blendMode !== BlendMode.LAYER || this._lastUsedFill === null;
        var useTemp = useBlend || nativeMSAA;
        if (useTemp) {
            target = SceneImage2D.getTemp(this.width, this.height, this._stage, nativeMSAA);
            // because target image is stupid filled - it not require merging
            if (this._lastUsedFill !== null) {
                // bitmap was filled plain, go clear TMP to this color too
                renderer.disableClear = false;
                renderer.view.backgroundColor = this._lastUsedFill;
                renderer.view.backgroundAlpha = (this._lastUsedFill >>> 24 & 0xff) / 0xff;
            }
            else {
                // we clear TMP and render to it, prepare to composing
                renderer.disableClear = false;
                renderer.view.backgroundColor = 0x0;
                renderer.view.backgroundAlpha = 0;
                // copy from source to tmp
                // TMP_POINT.setTo(0,0);
                // this._stage.copyPixels(this, target, this._rect, TMP_POINT, null, null, false);
            }
        }
        var transform = renderer.view.projection.transform;
        var mat3d = transform.matrix3D;
        mat3d.identity();
        if (matrix) {
            var raw = mat3d._rawData;
            raw[0] = matrix.a;
            raw[1] = matrix.b;
            raw[4] = matrix.c;
            raw[5] = matrix.d;
            raw[10] = 1;
            raw[12] = matrix.tx;
            raw[13] = matrix.ty;
        }
        // todo By this line we flip normals, and cull will be broken
        // 	NEED FIX THIS ASAP, or flip cull state
        mat3d.appendScale(1, -1, 1);
        mat3d.appendTranslation(0, this._rect.height, 1000);
        mat3d.invert();
        transform.matrix3D = mat3d;
        renderer.antiAlias = (internal ? this.antialiasQuality : target.antialiasQuality) || 0;
        renderer.view.target = target;
        renderer.view.projection.scale = 1000 / this._rect.height;
        // shift view, because target can be more
        renderer.view.projection.ratio = (this._rect.width / this._rect.height);
        renderer.view.x = -(target.width - this.width);
        renderer.view.y = -(target.height - this.height);
        renderer.view.width = this.width;
        renderer.view.height = this.height;
        var sourceNode = rootNode.addChildAt(source, 0);
        sourceNode.transformDisabled = true;
        // color transform should be enabled!
        sourceNode.colorTransformDisabled = false;
        root.transform.colorTransform = colorTransform;
        // anyway we not support this =))
        root.blendMode = SceneImage2D._mapSupportedBlendMode(blendMode);
        //save snapshot if unlocked
        //if (!this._locked)
        //SceneImage2D.scene.view.target=this;
        //SceneImage2D.scene.renderer.disableClear = !this._locked;
        renderer.render();
        // reset render to default value
        renderer.antiAlias = 0;
        renderer.disableClear = true;
        rootNode.removeChildAt(0);
        if (useTemp) {
            // because we copy MSAA into no msaa, it should passed as BLIT
            this._stage.filterManager.copyPixels(target, this, this._rect, TMP_POINT, useBlend, blendMode // apply blend mode if this needed.
            );
            SceneImage2D.tryStoreImage(target, this._stage);
        }
    };
    SceneImage2D.prototype.reset = function () { };
    SceneImage2D.assetType = '[image SceneImage2D]';
    return SceneImage2D;
}(BitmapImage2D));
export { SceneImage2D };
Stage.registerAbstraction(_Stage_BitmapImage2D, SceneImage2D);
